import type { ReactNode } from 'react'

/**
 * The one table in the product.
 *
 * Five screens list rows — contacts, tenants, suppressions, sends, consent —
 * and before this there were two copies of the same scaffolding with the third
 * about to be written. Each copy is a chance for one screen to lose the
 * horizontal scroll container, and a dense table that widens the document
 * breaks every other screen sharing the layout.
 *
 * It is deliberately not a data-grid. No sorting, no column config, no
 * virtualisation: none of that has a caller, and a generic table nobody needs
 * the general case of is how a component becomes the most expensive file in a
 * repository. It renders what it is given, with the two decisions that would
 * otherwise be forgotten — the scroll container, and a minimum width so columns
 * do not collapse into each other.
 */

/**
 * A column heading.
 *
 * A plain string for the headings that are only words, which is nearly all of
 * them. The object form is for a heading that is itself a control — a sort
 * button — and it carries its own key, because a header row whose columns can
 * be hidden cannot be keyed by position.
 */
export type HeadCell = string | { readonly key: string; readonly node: ReactNode }

export function Table({
  head,
  children,
  minWidth = 640,
}: {
  readonly head: readonly HeadCell[]
  readonly children: ReactNode
  /**
   * Below this the columns crush together, so the container scrolls instead.
   *
   * One of the widths below and nothing else, because the class has to exist in
   * the stylesheet. Typed as their union rather than `number`: the board asked
   * for 880, which was not in the table, and the fallback silently gave it 640
   * — a number a caller can name and not get is worse than one it cannot name.
   */
  readonly minWidth?: MinWidth
}) {
  return (
    <div className="overflow-x-auto rounded-card border border-line bg-surface">
      <table className={`w-full border-collapse text-left ${MIN_WIDTHS[minWidth]}`}>
        <thead>
          <tr className="border-line border-b">
            {head.map((cell) => (
              <th
                key={typeof cell === 'string' ? cell : cell.key}
                className="px-4 py-2.5 font-semibold text-[12px] text-t2 uppercase tracking-[0.04em]"
              >
                {typeof cell === 'string' ? cell : cell.node}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>{children}</tbody>
      </table>
    </div>
  )
}

/**
 * Literal classes, never assembled.
 *
 * Tailwind generates classes by scanning source text, so `min-w-[${n}px]` is a
 * class that does not exist in the stylesheet and silently renders at zero.
 */
const MIN_WIDTHS = {
  520: 'min-w-[520px]',
  640: 'min-w-[640px]',
  720: 'min-w-[720px]',
  760: 'min-w-[760px]',
  840: 'min-w-[840px]',
  880: 'min-w-[880px]',
} as const

/** The widths a caller may ask for. Anything else is not in the stylesheet. */
export type MinWidth = keyof typeof MIN_WIDTHS

/**
 * A row.
 *
 * `group` so a cell can reveal a control on hover — the edit button on the
 * contacts board is the caller that needs it, and the alternative is a hover
 * state held in React that re-renders the whole table as the pointer crosses
 * it.
 */
export function Tr({ children }: { readonly children: ReactNode }) {
  return (
    <tr className="group border-line-2 border-b last:border-0 hover:bg-surface-2">{children}</tr>
  )
}

/** A plain cell. `strong` is the column that identifies the row. */
export function Td({
  children,
  strong,
  mono,
}: {
  readonly children: ReactNode
  readonly strong?: boolean
  readonly mono?: boolean
}) {
  const tone = strong === true ? 'font-semibold text-t1' : 'text-t2'
  const face = mono === true ? 'font-mono' : ''
  return <td className={`px-4 py-2.5 text-[13px] ${tone} ${face}`}>{children}</td>
}

/** A figure. Tabular lining numerals so columns of digits line up. */
export function TdNum({ children }: { readonly children: string }) {
  return <td className="px-4 py-2.5 font-num text-[13px] text-t2 tabular-nums">{children}</td>
}

/**
 * What a board says when it has no rows, without losing its headings.
 *
 * A board used to swap its whole table for a card, which took the column
 * headings with it — and with them the `⋯` that renames, retypes and deletes a
 * column. An organisation with no segments yet, or anyone whose search matched
 * nothing, could therefore not manage the columns of the board in front of
 * them. The message belongs inside the table for the same reason the headings
 * do: the board is still the board when nobody is on it.
 *
 * `span` is how many columns there are, so the cell reaches across all of them
 * rather than sitting under the first.
 */
export function TrEmpty({
  span,
  children,
}: {
  readonly span: number
  readonly children: ReactNode
}) {
  return (
    <tr>
      <td className="px-4 py-12 text-center" colSpan={span}>
        {children}
      </td>
    </tr>
  )
}
